import { useEffect, useState } from 'react';
import UpIcon from '/asset/up.svg';
import { formatCurrency, removeSkuPrefix } from '../utils';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { PurchaseItem } from '../interfaces';
import { useLocation, useNavigate } from 'react-router-dom';
import { updatePurchaseCartAsync } from '../store/purchaseSlice';
import WhatsAppChatButton from './WhatsAppChatButton';

export default function CartFooter({
  showCart = true,
}: {
  showCart?: boolean;
}) {
  const location = useLocation();
  const { storeInfo } = useSelector((state: RootState) => state.products);
  const isAddressPickerRoute =
    location.pathname.split('/').filter(Boolean).pop() === 'address-picker';

  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const purchaseData = useSelector(
    (state: RootState) => state.purchase.purchase
  );
  const cart: PurchaseItem[] = purchaseData?.purchases ?? [];
  const totalAmount =
    purchaseData?.totalAmount ??
    cart.reduce((sum, item) => sum + (item.unitPrice ?? 0) * item.quantity, 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const navigate = useNavigate();
  const { routesPrefix } = useSelector((state: RootState) => state.setup);

  const handleDeleteItem = (index: number) => {
    const remaining = cart
      .filter((_, i) => i !== index)
      .map(({ quantity, productVariantId, specialRequest }) => ({
        quantity,
        productVariantId,
        specialRequest,
      }));
    dispatch(
      updatePurchaseCartAsync({
        CartLineItems: remaining,
        purchaseCode: purchaseData?.purchaseCode ?? null,
        silent: true,
      })
    );
  };

  const handleUpdateQuantity = (index: number, delta: number) => {
    const item = cart[index];
    const newQty = item.quantity + delta;
    if (newQty <= 0) {
      handleDeleteItem(index);
      return;
    }
    const updated = cart.map((c, i) => ({
      quantity: i === index ? newQty : c.quantity,
      productVariantId: c.productVariantId,
      specialRequest: c.specialRequest,
    }));
    dispatch(
      updatePurchaseCartAsync({
        CartLineItems: updated,
        purchaseCode: purchaseData?.purchaseCode ?? null,
        silent: true,
      })
    );
  };

  useEffect(() => {
    if (cart.length === 0) {
      setExpanded(false);
    }
  }, [cart.length]);

  return (
    <div
      className="fixed bottom-0 w-full
        z-[50]"
    >
      <div className="relative">
        {storeInfo?.isMimicEnabled && !isAddressPickerRoute && (
          <WhatsAppChatButton
            phoneNumber={storeInfo.mimicWhatsAppPhoneNumber}
          />
        )}

        {showCart && cart.length > 0 && (
          <div>
            <button
              type="button"
              className="
                  bg-secondary-500 flex justify-between cursor-pointer
                  relative items-center py-[10px] px-[20px] rounded-t-3xl
                  w-full cursor-pointer
                  outline-none focus:outline-none focus-visible:outline-none active:outline-none
                  "
              aria-expanded={expanded}
              aria-controls="cart-summary-panel"
              aria-label={
                expanded ? 'Collapse cart summary' : 'Expand cart summary'
              }
              onClick={() => setExpanded((prev) => !prev)}
            >
              <div
                className="w-[250px] h-[70px] rounded-md
                flex justify-center items-center p-[3px] gap-[15px]"
              >
                <div
                  className="w-[50px] h-full
                flex flex-col justify-center"
                >
                  <div
                    className="w-[50px] h-[50px]
                                text-white bg-secondary-800 rounded-full
                                text-xl text-center
                                flex items-center justify-center
                                font-bold"
                  >
                    {itemCount > 9 ? '9+' : itemCount}
                  </div>
                </div>
                <div
                  className="w-full h-full rounded-md
                flex flex-col content-center justify-center"
                >
                  <div className="text-left text-base text-white font-bold">
                    Cart Summary
                  </div>
                  <div className="text-left text-sm text-white text-secondary-200">
                    {cart.length} {cart.length === 1 ? 'Item' : 'Items'}
                  </div>
                </div>
              </div>
              <div
                className="max-w-[170px] h-[70px]
              rounded-md flex gap-[10px] p-[5px]"
              >
                <div
                  className="h-full flex flex-col 
                    text-white text-sm justify-center"
                >
                  {formatCurrency(totalAmount)}
                </div>
                <div
                  className="
                  h-full
                  flex 
                  content-center"
                >
                  <img
                    src={UpIcon}
                    alt="toggle cart"
                    className={`transition-transform duration-200 
                    w-[25px] ${expanded ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>
            </button>
            <div
              className={`grid bg-white transition-[grid-template-rows] duration-300 ease-in-out
              ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div
                className={`overflow-hidden transition-opacity duration-300 ease-in-out
                ${expanded ? 'opacity-100 delay-100' : 'opacity-0'}`}
              >
                <div
                  className="w-full max-h-[250px]
                        overflow-y-auto px-[20px] py-[20px] 
                        space-y-[8px] thin-light-scrollbar"
                >
                  {cart.map((item: PurchaseItem, index: number) => (
                    <div
                      key={item.productVariantId}
                      className="relative flex items-center gap-[12px]
                      bg-white shadow-sm rounded-lg p-[10px] pr-[10px]
                      border-[1px] border-solid border-secondary-50"
                    >
                      {/* Delete — top-right corner */}
                      <button
                        onClick={() => handleDeleteItem(index)}
                        className="absolute top-1.5 right-1.5 
                        p-0 rounded-full
                        text-gray-300 hover:text-red-400 
                        bg-primary-50 
                        text-primary-500
                        hover:bg-red-50
                        transition-colors"
                        aria-label="Remove item"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>

                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-[50px] h-[50px] rounded-md object-contain object-center flex-shrink-0"
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="text-gray-800 text-[13px] font-medium truncate pr-4">
                          {item.name}
                        </div>
                        {item.sku && (
                          <div className="text-gray-400 font-bold text-[10px]">
                            {removeSkuPrefix(item.sku)}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-[6px]">
                          {/* +/− quantity controls */}
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleUpdateQuantity(index, -1)}
                              className="bg-transparent w-[22px] h-[22px] rounded-md
                              border border-gray-200 p-0
                              flex items-center justify-center text-secondary-500
                              outline-none focus:outline-none focus-visible:outline-none active:outline-none
                              transition-colors cursor-pointer"
                              aria-label="Decrease quantity"
                            >
                              <svg
                                width="16"
                                height="10"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#222222"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              >
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            </button>
                            <span className="text-gray-800 text-[12px] font-bold w-[20px] text-center">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleUpdateQuantity(index, 1)}
                              className="
                              flex justify-center content-center align-center items-center
                              bg-transparent w-[22px] h-[22px] rounded-md border border-gray-200
                              p-0 flex items-center justify-center text-secondary-500
                              outline-none focus:outline-none focus-visible:outline-none active:outline-none"
                              aria-label="Increase quantity"
                            >
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#222222"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            </button>
                          </div>
                          <div className="text-secondary-500 font-bold text-[13px]">
                            {formatCurrency(
                              (item.unitPrice ?? 0) * item.quantity
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className="px-[20px] py-[20px] bg-white
                  border-t-2 border-t-secondary-200 border-t-solid"
                >
                  <button
                    className="w-full 
                            bg-secondary-700 text-white py-[10px] rounded-lg"
                    onClick={() => navigate(`${routesPrefix}cart`)}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
